import { useState, useEffect } from 'react'
import { useAtom } from 'jotai'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material'
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ArrowForward as ArrowForwardIcon,
  Description as ResumeIcon,
  Group as TeamIcon,
  EmailOutlined as EmailIcon,
  Event as EventIcon,
} from '@mui/icons-material'
import { clientsApi, clientStageTasksApi, ClientStageTasks } from '../../services/api'
import { stageEntryEmailOption } from '../../constants/stageEntryEmails'
import { useNotification } from '../../hooks/useNotification'
import { userAtom } from '../../state/auth'
import { isAdminOrManager } from '../../utils/roles'
import { missingTeamRoles } from '../../utils/clientTeam'

interface AdvanceStageDialogProps {
  open: boolean
  onClose: () => void
  clientId: string
  clientName: string
  currentStage: string
  targetStage: string
  currentStageLabel: string
  targetStageLabel: string
  onAdvanced: () => void
  resumeApproved?: boolean
  internalResumeApproved?: boolean
  internalApprovedBy?: string
  serviceAgreementApproved?: boolean
  teamAssignment?: {
    clientSuccess?: string
    resumeWriter?: string
    coach?: string
  }
  onAssignTeam?: () => void
  // MWA-391 — the email the target stage is set to send (its Stage Gates setting).
  entryEmail?: string | null
}

interface IncompleteTask {
  id: string
  title: string
  description?: string
}

export default function AdvanceStageDialog({
  open,
  onClose,
  clientId,
  clientName,
  currentStage,
  targetStage,
  currentStageLabel,
  targetStageLabel,
  onAdvanced,
  resumeApproved,
  internalResumeApproved,
  internalApprovedBy,
  serviceAgreementApproved,
  teamAssignment,
  onAssignTeam,
  entryEmail,
}: AdvanceStageDialogProps) {
  const entryEmailOption = stageEntryEmailOption(entryEmail)
  const [user] = useAtom(userAtom)
  const { showSuccess, showError } = useNotification()
  const [loading, setLoading] = useState(true)
  const [advancing, setAdvancing] = useState(false)
  const [, setStageTasks] = useState<ClientStageTasks | null>(null)
  const [incompleteTasks, setIncompleteTasks] = useState<IncompleteTask[]>([])
  const [allowOverride, setAllowOverride] = useState(true)
  const [overrideReason, setOverrideReason] = useState('')
  const [showOverrideForm, setShowOverrideForm] = useState(false)

  const isAdmin = isAdminOrManager(user)
  // isManager merged into isAdmin via isAdminOrManager helper
  const canOverride = isAdmin

  useEffect(() => {
    if (open) {
      console.log('[AdvanceStageDialog] Dialog opened - v2.0 with Assign Team button')
      console.log('[AdvanceStageDialog] onAssignTeam callback:', onAssignTeam ? 'PROVIDED' : 'MISSING')
      loadStageTasks()
    }
  }, [open, clientId, currentStage])

  const loadStageTasks = async () => {
    try {
      setLoading(true)
      setShowOverrideForm(false)
      setOverrideReason('')

      const result = await clientStageTasksApi.get(clientId, currentStage)
      const tasks = Array.isArray(result) ? result[0] : result

      if (tasks) {
        setStageTasks(tasks)
        const incomplete = tasks.tasks.filter((t) => t.required && !t.completed)
        setIncompleteTasks(
          incomplete.map((t) => ({
            id: t.gateTaskId,
            title: t.title,
            description: t.description,
          }))
        )
      } else {
        setStageTasks(null)
        setIncompleteTasks([])
      }
    } catch (err) {
      console.error('Failed to load stage tasks:', err)
      // No tasks configured - allow advancing
      setStageTasks(null)
      setIncompleteTasks([])
    } finally {
      setLoading(false)
    }
  }

  const handleAdvance = async (useOverride: boolean = false) => {
    try {
      setAdvancing(true)

      const options: { notes?: string; override?: boolean; overrideReason?: string } = {}

      if (useOverride) {
        options.override = true
        options.overrideReason = overrideReason
      }

      await clientsApi.advanceWorkflowStage(clientId, targetStage as 'onboarding', options)
      showSuccess(`${clientName} advanced to ${targetStageLabel}`)
      onAdvanced()
      onClose()
    } catch (err: unknown) {
      console.error('Failed to advance stage:', err)

      // Check if it's an incomplete tasks error
      if (err instanceof Error && err.message.includes('incomplete_tasks')) {
        try {
          const errorData = JSON.parse(err.message)
          if (errorData.incompleteTasks) {
            setIncompleteTasks(errorData.incompleteTasks)
            setAllowOverride(errorData.allowOverride !== false)
            setShowOverrideForm(true)
            return
          }
        } catch {
          // Not a JSON error, show generic message
        }
      }

      showError('Failed to advance stage')
    } finally {
      setAdvancing(false)
    }
  }

  const handleClose = () => {
    setShowOverrideForm(false)
    setOverrideReason('')
    onClose()
  }

  // Check resume approval when moving from deliverables_in_progress → deliverables_complete
  const isDeliverablesAdvance = currentStage === 'deliverables_in_progress' && targetStage === 'deliverables_complete'
  const resumeApprovalNeeded = isDeliverablesAdvance && !resumeApproved

  // Service agreement approval gate when advancing out of onboarding. It feeds
  // allComplete, so an unapproved agreement replaces the plain Advance button
  // with the manager-only Override form — the same weight as the resume
  // approval gate above. The copy used to read like advice, which understated
  // it.
  const isOnboardingAdvance = currentStage === 'onboarding'
  const serviceAgreementNeeded = isOnboardingAdvance && !serviceAgreementApproved

  // Check team assignment
  const hasTeamAssigned = !!(teamAssignment?.clientSuccess || teamAssignment?.resumeWriter || teamAssignment?.coach)
  const missingRoles = missingTeamRoles(teamAssignment)

  const allComplete = incompleteTasks.length === 0 && !resumeApprovalNeeded && !serviceAgreementNeeded && hasTeamAssigned

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArrowForwardIcon color="primary" />
          Advance Stage
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="body1">
                Moving <strong>{clientName}</strong> from{' '}
                <strong>{currentStageLabel}</strong> to <strong>{targetStageLabel}</strong>
              </Typography>
            </Box>

            {/* Team Assignment Status */}
            {!hasTeamAssigned ? (
              <Alert
                severity="error"
                icon={<TeamIcon />}
                sx={{ mb: 2 }}
                action={
                  onAssignTeam && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={onAssignTeam}
                      startIcon={<TeamIcon />}
                      sx={{
                        whiteSpace: 'nowrap',
                        bgcolor: '#d32f2f',
                        color: 'white',
                        fontWeight: 600,
                        boxShadow: 2,
                        '&:hover': {
                          bgcolor: '#c62828',
                          boxShadow: 3,
                        }
                      }}
                    >
                      Assign
                    </Button>
                  )
                }
              >
                <strong>No team assigned.</strong> Team members must be assigned so they are notified as the client progresses through stages.
                {missingRoles.length > 0 && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Missing: {missingRoles.join(', ')}
                  </Typography>
                )}
              </Alert>
            ) : missingRoles.length > 0 ? (
              <Alert
                severity="warning"
                icon={<TeamIcon />}
                sx={{ mb: 2 }}
                action={
                  isAdmin && onAssignTeam && (
                    <Button
                      color="inherit"
                      variant="outlined"
                      size="small"
                      onClick={onAssignTeam}
                      startIcon={<TeamIcon />}
                      sx={{ whiteSpace: 'nowrap', minHeight: 44 }}
                    >
                      Assign
                    </Button>
                  )
                }
              >
                Some team roles are not assigned: <strong>{missingRoles.join(', ')}</strong>
              </Alert>
            ) : (
              <Alert severity="success" icon={<TeamIcon />} sx={{ mb: 2 }}>
                All team roles are assigned.
              </Alert>
            )}

            {/* Resume Approval Status - only for deliverables_in_progress → deliverables_complete */}
            {isDeliverablesAdvance && (
              <Box sx={{ mb: 2 }}>
                {internalResumeApproved ? (
                  <Alert severity="success" icon={<ResumeIcon />} sx={{ mb: 1 }}>
                    Resume internally approved by {internalApprovedBy || 'staff'}
                  </Alert>
                ) : (
                  <Alert severity="info" icon={<ResumeIcon />} sx={{ mb: 1 }}>
                    Resume has <strong>not been internally approved</strong> yet
                  </Alert>
                )}
                {resumeApproved ? (
                  <Alert severity="success" icon={<ResumeIcon />}>
                    Resume has been approved by the client
                  </Alert>
                ) : (
                  <Alert severity="warning" icon={<ResumeIcon />}>
                    Resume has <strong>not been approved</strong> by the client yet
                  </Alert>
                )}
              </Box>
            )}

            {/* Service Agreement approval - only when advancing out of Onboarding */}
            {isOnboardingAdvance && (
              serviceAgreementApproved ? (
                <Alert severity="success" icon={<ResumeIcon />} sx={{ mb: 2 }}>
                  Service agreement has been approved by the client.
                </Alert>
              ) : (
                <Alert severity="warning" icon={<ResumeIcon />} sx={{ mb: 2 }}>
                  Service agreement has <strong>not been marked approved</strong>. Mark it approved to advance, or ask a manager to override with a reason.
                </Alert>
              )
            )}

            {/* MWA-391 — the notice follows the target stage's own email setting in
                Stage Gates, never a hard-coded pair of stage ids. */}
            {entryEmailOption && (
              <Alert severity="info" icon={<EmailIcon />} sx={{ mb: 2 }}>
                Moving this client to <strong>{targetStageLabel}</strong> will {entryEmailOption.notice}
              </Alert>
            )}

            {/* Workshop Scheduling Reminder - only for deliverables_complete → linkedin_workshop */}
            {currentStage === 'deliverables_complete' && targetStage === 'linkedin_workshop' && (
              <Alert severity="info" icon={<EventIcon />} sx={{ mb: 2 }}>
                Ensure the LinkedIn Workshop is scheduled with the client before advancing this stage.
              </Alert>
            )}

            {allComplete ? (
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                All checks passed. Ready to advance!
              </Alert>
            ) : (
              <>
                {incompleteTasks.length > 0 && (
                  <>
                    <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                      {incompleteTasks.length} required task(s) are incomplete
                    </Alert>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Incomplete Required Tasks:
                    </Typography>

                    <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                      {incompleteTasks.map((task) => (
                        <ListItem key={task.id}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CancelIcon sx={{ color: 'error.main', fontSize: 18 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={task.title}
                            secondary={task.description}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}

                {showOverrideForm || !allComplete ? (
                  canOverride && allowOverride ? (
                    <Box>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Override (Managers Only)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        As a manager, you can override and advance anyway. This will be logged and
                        managers will be notified.
                      </Typography>
                      <TextField
                        label="Override Reason"
                        value={overrideReason}
                        onChange={(e) => setOverrideReason(e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        placeholder={
                          !hasTeamAssigned
                            ? "Explain why you're advancing without a team assigned..."
                            : serviceAgreementNeeded && incompleteTasks.length === 0
                              ? "Explain why you're advancing without service agreement approval..."
                              : resumeApprovalNeeded && incompleteTasks.length === 0
                                ? "Explain why you're advancing without resume approval..."
                                : "Explain why you're overriding incomplete tasks..."
                        }
                        required
                        error={showOverrideForm && !overrideReason.trim()}
                        helperText={
                          showOverrideForm && !overrideReason.trim()
                            ? 'Override reason is required'
                            : ''
                        }
                      />
                    </Box>
                  ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {!allowOverride
                        ? 'Override is not allowed for this stage. Please complete all required tasks before advancing.'
                        : 'Only managers can override incomplete tasks. Please complete the tasks or contact a manager.'}
                    </Alert>
                  )
                ) : null}
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={advancing}>
          Cancel
        </Button>
        {allComplete ? (
          <Button
            variant="contained"
            onClick={() => handleAdvance(false)}
            disabled={advancing || loading}
          >
            {advancing ? <CircularProgress size={20} color="inherit" /> : 'Advance Stage'}
          </Button>
        ) : canOverride && allowOverride ? (
          <Button
            variant="contained"
            color="warning"
            onClick={() => handleAdvance(true)}
            disabled={advancing || loading || !overrideReason.trim()}
          >
            {advancing ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Override & Advance'
            )}
          </Button>
        ) : (
          <Button variant="contained" disabled>
            Complete Tasks First
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
